import { z } from "zod";

export const userRoleSchema = z.enum(["member", "system_admin"]);
export const userStatusSchema = z.enum(["active", "disabled"]);
export const usernameSchema = z
  .string()
  .trim()
  .min(3)
  .max(32)
  .regex(/^[A-Za-z0-9._-]+$/u);
export const passwordSchema = z.string().min(12).max(128);

const entityNameSchema = z.string().trim().min(1).max(120);
const versionSchema = z.number().int().positive();
const timestampSchema = z.iso.datetime();

export const userSummarySchema = z.strictObject({
  id: z.uuid(),
  username: usernameSchema,
  role: userRoleSchema,
  status: userStatusSchema,
  mustChangePassword: z.boolean()
});

export const sessionViewSchema = z.strictObject({
  user: userSummarySchema,
  csrfToken: z.string().min(1)
});

export const projectFolderViewSchema = z.strictObject({
  id: z.uuid(),
  ownerId: z.uuid(),
  name: entityNameSchema,
  version: versionSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema
});

export const projectViewSchema = z.strictObject({
  id: z.uuid(),
  ownerId: z.uuid(),
  folderId: z.uuid().nullable(),
  canvasId: z.uuid(),
  name: entityNameSchema,
  version: versionSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema
});

export const loginRequestSchema = z.strictObject({
  username: usernameSchema,
  password: passwordSchema
});

export const passwordChangeRequestSchema = z.strictObject({
  currentPassword: passwordSchema,
  newPassword: passwordSchema
});

export const accountCreateRequestSchema = z.strictObject({
  username: usernameSchema,
  password: passwordSchema,
  role: userRoleSchema
});

export const folderCreateRequestSchema = z.strictObject({
  name: entityNameSchema
});

export const createProjectRequestSchema = z.strictObject({
  name: entityNameSchema,
  folderId: z.uuid().nullable().optional()
});

export const projectRenameRequestSchema = z.strictObject({
  name: entityNameSchema,
  version: versionSchema
});

export type UserRole = z.infer<typeof userRoleSchema>;
export type UserStatus = z.infer<typeof userStatusSchema>;
export type UserSummary = z.infer<typeof userSummarySchema>;
export type SessionView = z.infer<typeof sessionViewSchema>;
export type ProjectFolderView = z.infer<typeof projectFolderViewSchema>;
export type ProjectView = z.infer<typeof projectViewSchema>;
export type LoginRequest = z.infer<typeof loginRequestSchema>;
export type PasswordChangeRequest = z.infer<typeof passwordChangeRequestSchema>;
export type AccountCreateRequest = z.infer<typeof accountCreateRequestSchema>;
export type FolderCreateRequest = z.infer<typeof folderCreateRequestSchema>;
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;
export type ProjectRenameRequest = z.infer<typeof projectRenameRequestSchema>;
